<template>
  <vs-popup
    :title="step === 'email' ? 'E-posta Adresi' : 'Şifre Yenileme'"
    :active.sync="isActive"
    :button-close-hidden="step === 'email'"
  >
    <template v-if="step === 'password'">
      <p class="mb-4">
        Şifrenizin geçerlilik süresi ({{ validityMonths }} ay) doldu. Devam etmek için yeni bir şifre belirleyin.
        Yeni şifre son kullandığınız şifreyle aynı olamaz.
      </p>
      <vs-input
        :type="showNewPassword ? 'text' : 'password'"
        label="Yeni Şifre"
        v-model="newPassword"
        class="w-full"
        icon-pack="feather"
        :icon="showNewPassword ? 'icon-eye-off' : 'icon-eye'"
        icon-after
        icon-no-border
        @click.native="onPasswordIconClick($event, 'newPassword')"
      />
      <vs-input
        :type="showRePassword ? 'text' : 'password'"
        label="Yeni Şifre Tekrar"
        v-model="rePassword"
        class="w-full mt-4"
        icon-pack="feather"
        :icon="showRePassword ? 'icon-eye-off' : 'icon-eye'"
        icon-after
        icon-no-border
        @click.native="onPasswordIconClick($event, 'rePassword')"
        @keyup.enter.stop="submit"
      />
      <span class="text-danger text-sm">{{ validationMessage }}</span>
      <vs-row vs-type="flex" vs-justify="flex-end" class="mt-4">
        <vs-button color="success" :disabled="busy" @click="submit">Şifreyi Yenile</vs-button>
      </vs-row>
    </template>

    <template v-else>
      <p class="mb-4">
        Şifreniz yenilendi. Hesabınızda kayıtlı e-posta adresi yok; devam etmek için e-posta adresinizi girin.
      </p>
      <vs-input
        type="email"
        label="E-posta"
        v-model="email"
        class="w-full"
        icon-pack="feather"
        icon="icon-mail"
        @keyup.enter.stop="submitEmail"
      />
      <span class="text-danger text-sm">{{ emailValidationMessage }}</span>
      <vs-row vs-type="flex" vs-justify="flex-end" class="mt-4">
        <vs-button color="success" :disabled="busy" @click="submitEmail">Kaydet ve Giriş Yap</vs-button>
      </vs-row>
    </template>
  </vs-popup>
</template>

<script>
import jwt from "@/http/requests/auth/jwt/index.js";

// NewUserAdd.vue passwordPolicy kuralıyla aynı; sunucu da aynı kuralı uygular.
function passwordPolicyError(value) {
  if (!value) return "";
  const ok =
    value.length >= 8 &&
    /[A-ZÇĞİÖŞÜ]/.test(value) &&
    /[a-zçğıöşü]/.test(value) &&
    /[0-9]/.test(value) &&
    /[^A-Za-z0-9çğıöşüÇĞİÖŞÜ]/.test(value);
  return ok
    ? ""
    : "Şifre en az 8 karakter olmalı; en az 1 büyük harf, 1 küçük harf, 1 rakam ve 1 özel karakter içermelidir.";
}

function errorText(res, fallback) {
  const message = res && res.data && res.data.errors && res.data.errors[0] && res.data.errors[0].message;
  if (message === "INVALID_LOGIN_ATTEMPT") return "Mevcut şifre doğrulanamadı.";
  return message || fallback;
}

export default {
  props: {
    active: { type: Boolean, required: true },
    userName: { type: String, default: null },
    currentPassword: { type: String, default: null },
  },
  data() {
    return {
      step: "password",
      newPassword: "",
      rePassword: "",
      email: "",
      busy: false,
      done: false,
      showNewPassword: false,
      showRePassword: false,
      validityMonths: 3,
    };
  },
  computed: {
    isActive: {
      get() {
        return this.active;
      },
      set(val) {
        this.$emit("update:active", val);
      },
    },
    validationMessage() {
      const policy = passwordPolicyError(this.newPassword);
      if (policy) return policy;
      if (this.rePassword && this.rePassword !== this.newPassword) return "Şifreler uyuşmuyor.";
      return "";
    },
    emailValidationMessage() {
      if (!this.email) return "";
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim()) ? "" : "Geçerli bir e-posta adresi giriniz.";
    },
  },
  watch: {
    active(val) {
      const emailPending = this.step === "email" && !this.done;
      if (!val) {
        // E-posta zorunlu: adım tamamlanmadan kapatılırsa (arka plana tıklama vb.) yeniden aç.
        if (emailPending) this.$nextTick(() => this.$emit("update:active", true));
        return;
      }
      if (emailPending) return;
      this.step = "password";
      this.done = false;
      this.newPassword = "";
      this.rePassword = "";
      this.email = "";
      this.showNewPassword = false;
      this.showRePassword = false;
    },
  },
  methods: {
    onPasswordIconClick(event, which) {
      if (!event.target.closest || !event.target.closest(".vs-input--icon")) return;
      if (which === "newPassword") this.showNewPassword = !this.showNewPassword;
      else this.showRePassword = !this.showRePassword;
    },
    notifyError(title, text) {
      this.$vs.notify({ title, text, iconPack: "feather", icon: "icon-alert-circle", color: "danger" });
    },
    finish() {
      this.done = true;
      this.isActive = false;
      this.$emit("changed", this.newPassword);
    },
    submit() {
      if (this.busy || !this.newPassword || this.validationMessage || this.rePassword !== this.newPassword) return;
      this.busy = true;
      jwt
        .changeExpiredPassword(this.userName, this.currentPassword, this.newPassword)
        .then((res) => {
          if (!res || !res.data.result) {
            this.notifyError("Şifre yenilenemedi", errorText(res, "İşlem başarısız."));
            return;
          }
          this.$vs.notify({ title: "Başarılı", text: "Şifreniz yenilendi.", color: "success" });
          if (res.data.data && res.data.data.emailRequired) {
            this.step = "email";
            return;
          }
          this.finish();
        })
        .catch(() => this.notifyError("Hata !", "Sunucu Bağlantı Hatası"))
        .finally(() => {
          this.busy = false;
        });
    },
    submitEmail() {
      if (this.busy || !this.email || this.emailValidationMessage) return;
      this.busy = true;
      jwt
        .setMissingEmail(this.userName, this.newPassword, this.email.trim())
        .then((res) => {
          if (!res || !res.data.result) {
            this.notifyError("E-posta kaydedilemedi", errorText(res, "İşlem başarısız."));
            return;
          }
          this.finish();
        })
        .catch(() => this.notifyError("Hata !", "Sunucu Bağlantı Hatası"))
        .finally(() => {
          this.busy = false;
        });
    },
  },
};
</script>
